/**
 * EditionContext
 *
 * Provides edition-awareness (CONSUMER vs CORPORATE) to the entire app so the
 * UI can adapt terminology and plan visibility WITHOUT forking the codebase.
 *
 * Edition is resolved in priority order:
 *   1. The active subscription's service-plan edition (source of truth once the
 *      account has subscribed — e.g. an smb-* plan implies CORPORATE).
 *   2. The account's own `account_type` (CONSUMER/CORPORATE) loaded from the
 *      account API — authoritative for the account itself and used before a
 *      subscription exists (e.g. an Individual/Family account on "No Plan").
 *   3. The preference chosen at sign-up and persisted to localStorage
 *      ("accountEdition") — used before the account record has loaded.
 *   4. CONSUMER (safe default, preserves existing behavior).
 *
 * Terminology helper:
 *   const { term } = useEdition();
 *   term("member")  // "Family Member" (consumer) or "Team Member" (corporate)
 * Terms are resolved through the i18n dictionary using keys of the shape
 * `edition.<consumer|corporate>.<key>`, so they are also language-aware.
 *
 * @module EditionContext
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useSubscription } from "./SubscriptionContext";
import { useUser } from "./UserContext";
import AccountAPI from "../services/AccountAPI";
import { useTranslation } from "../../../i18n/LanguageContext";

export const EDITIONS = {
  CONSUMER: "CONSUMER",
  CORPORATE: "CORPORATE",
};

const STORAGE_KEY = "accountEdition";

const EditionContext = createContext();

/**
 * Custom hook to consume the EditionContext.
 * @throws {Error} If used outside of an EditionProvider
 */
export const useEdition = () => {
  const context = useContext(EditionContext);
  if (!context) {
    throw new Error("useEdition must be used within an EditionProvider");
  }
  return context;
};

/** Normalize any raw value into one of the supported edition discriminators. */
const normalizeEdition = (value) => {
  if (!value) return null;
  const upper = String(value).toUpperCase();
  return upper === EDITIONS.CORPORATE ? EDITIONS.CORPORATE : EDITIONS.CONSUMER;
};

/**
 * EditionProvider — must be rendered inside SubscriptionProvider (it derives the
 * edition from the active subscription's plan) and inside LanguageProvider (the
 * `term` helper reads the i18n dictionary).
 */
export const EditionProvider = ({ children }) => {
  const { servicePlan } = useSubscription();
  const { loggedInUser } = useUser();
  const { t } = useTranslation();

  const [editionPreference, setEditionPreferenceState] = useState(() => {
    try {
      return normalizeEdition(localStorage.getItem(STORAGE_KEY));
    } catch {
      return null;
    }
  });

  // Edition declared by the account record itself (account_type). This is the
  // authoritative signal for the account before any subscription exists — it
  // prevents a stale localStorage preference from leaking the wrong plan set
  // (e.g. an Individual/Family account on "No Plan" being shown corporate plans).
  const [accountTypeEdition, setAccountTypeEdition] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!loggedInUser?.uuid) {
      setAccountTypeEdition(null);
      return;
    }

    (async () => {
      try {
        const response = await AccountAPI.getAccountByUserUuid();
        const accountInfo = response?.data || response;
        const accountType =
          accountInfo?.accountType || accountInfo?.account_type || null;
        if (cancelled) return;
        setAccountTypeEdition(normalizeEdition(accountType));
      } catch {
        // Non-fatal — fall back to subscription plan / localStorage preference.
        if (!cancelled) setAccountTypeEdition(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loggedInUser?.uuid]);

  // Edition implied by the active subscription's service plan. Prefer the
  // explicit `edition` field, fall back to detecting the smb-* plan-name prefix.
  const planEdition = useMemo(() => {
    if (!servicePlan) return null;
    if (servicePlan.edition) return normalizeEdition(servicePlan.edition);
    const planName = String(servicePlan.planName || "").toLowerCase();
    if (planName.startsWith("smb-")) return EDITIONS.CORPORATE;
    return null;
  }, [servicePlan]);

  // Once a definitive edition is known (from the subscription plan or the
  // account record), keep the persisted preference in sync so it survives a
  // logout/login before re-fetching.
  useEffect(() => {
    const definitive = planEdition || accountTypeEdition;
    if (!definitive) return;
    try {
      localStorage.setItem(STORAGE_KEY, definitive);
    } catch {
      /* localStorage unavailable — non-fatal */
    }
    setEditionPreferenceState(definitive);
  }, [planEdition, accountTypeEdition]);

  const edition =
    planEdition || accountTypeEdition || editionPreference || EDITIONS.CONSUMER;
  const isCorporate = edition === EDITIONS.CORPORATE;

  /**
   * Record the edition the user selected at sign-up (before any subscription
   * exists). Persisted so the post-registration experience uses the right
   * terminology immediately.
   */
  const setEditionPreference = useCallback((value) => {
    const normalized = normalizeEdition(value) || EDITIONS.CONSUMER;
    try {
      localStorage.setItem(STORAGE_KEY, normalized);
    } catch {
      /* localStorage unavailable — non-fatal */
    }
    setEditionPreferenceState(normalized);
  }, []);

  /** Clear any persisted edition preference (e.g. on logout). */
  const clearEditionPreference = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* localStorage unavailable — non-fatal */
    }
    setEditionPreferenceState(null);
  }, []);

  /**
   * Resolve an edition-aware term via the i18n dictionary.
   * @param {string} key e.g. "member", "members", "owner", "group", "account"
   * @returns {string} localized, edition-specific word
   */
  const term = useCallback(
    (key) => {
      const scope = isCorporate ? "corporate" : "consumer";
      return t(`edition.${scope}.${key}`);
    },
    [isCorporate, t],
  );

  const value = useMemo(
    () => ({
      edition,
      isCorporate,
      isConsumer: !isCorporate,
      setEditionPreference,
      clearEditionPreference,
      term,
      EDITIONS,
    }),
    [edition, isCorporate, setEditionPreference, clearEditionPreference, term],
  );

  return (
    <EditionContext.Provider value={value}>{children}</EditionContext.Provider>
  );
};

export default EditionContext;
